import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { ErrorState, LoadingState } from "../../components/Ui";
import { getLandingPage } from "../../lib/api";
import {
  applyPageMetadata,
  buildLandingStructuredData,
  DEFAULT_OG_IMAGE,
  DEFAULT_SEO,
} from "../../lib/seo";
import { PublicNavbar } from "./PublicNavbar";
import {
  AboutSection,
  CapabilitiesSection,
  FinalCtaSection,
  HeroSection,
  PhilosophySection,
  ProductsSection,
  SiteFooter,
  StatisticsSection,
} from "./LandingSections";
import { useScrollReveal } from "./useScrollReveal";

export function LandingPage() {
  const query = useQuery({ queryKey: ["public-landing"], queryFn: getLandingPage });
  const data = query.data;
  useScrollReveal(Boolean(data));

  useEffect(() => {
    if (!data) return;
    applyPageMetadata({
      title: data.site.seo.title || DEFAULT_SEO.title,
      description: data.site.seo.description || DEFAULT_SEO.description,
      canonicalPath: "/",
      image: data.site.seo.ogImageUrl || DEFAULT_OG_IMAGE,
      imageAlt:
        "Steria House — designing intelligent experiences for people and human growth",
      structuredData: buildLandingStructuredData(
        data.products.map((product) => ({ name: product.name, slug: product.slug })),
      ),
    });
  }, [data]);

  if (query.isLoading) return <LoadingState />;
  if (query.isError || !data) {
    return (
      <ErrorState
        message="The content service is not available yet. Start the Steria House API and try again."
        onRetry={() => query.refetch()}
      />
    );
  }

  return (
    <>
      <PublicNavbar items={data.navigation} />
      <main>
        <HeroSection content={data.site.hero} />
        <AboutSection content={data.site.about} />
        <PhilosophySection content={data.site.philosophy} principles={data.principles} />
        <ProductsSection content={data.site.products} products={data.products} />
        <CapabilitiesSection content={data.site.capabilities} services={data.services} />
        <StatisticsSection content={data.site.statistics} statistics={data.statistics} />
        <FinalCtaSection content={data.site.cta} socialLinks={data.socialLinks} />
      </main>
      <SiteFooter
        content={data.site.footer}
        navigation={data.navigation}
        products={data.products}
        socialLinks={data.socialLinks}
      />
    </>
  );
}
